import type { ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChildren,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  linkedSignal,
  Output,
  QueryList,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import _ from 'lodash';
import { debounce, filter, race, Subject, tap, timer } from 'rxjs';

import { AutocompleteTemplateComponent } from './autocomplete-template/autocomplete-template.component';

@UntilDestroy()
@Component({
  selector: 'spa-autocomplete-search',
  templateUrl: 'autocomplete-search.component.html',
  styleUrls: ['autocomplete-search.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteSearchComponent<T> {
  private readonly transloco = inject(TranslocoService);

  private readonly searchInput = viewChild.required<unknown, ElementRef<HTMLElement>>('searchInput', {
    read: ElementRef,
  });

  label = input<string>(this.transloco.translate('label.booking.guest-search'));
  isReadOnly = input<boolean>(false);
  @Input() checkboxSelectionType: 'single' | 'multiple' | 'none' = 'none';
  @Input() isSearching = false;
  @Input() isRequired = false;
  readonly rows = input<AutocompleteRow<T>[] | null>(null);
  protected readonly localRows = linkedSignal(() => this.rows());
  @Input() columns: AutocompleteColumn<T>[] = [];
  @Output() fetchData = new EventEmitter<string | null>();
  @Output() selectRow = new EventEmitter<T[] | undefined>();
  @ContentChildren(AutocompleteTemplateComponent) columnTemplates!: QueryList<AutocompleteTemplateComponent>;
  searchTermChange$ = new Subject<string>();
  private readonly enterPressed$ = new Subject<void>();
  protected readonly searchTerm = signal<string | null>(null);
  SEARCH_TERM_MIN_LENGTH = 2;
  icon = computed(() => (this.isReadOnly() ? '' : 'search'));

  protected readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  protected readonly applyDisabled = computed(() => {
    const rows = this.localRows();
    return !rows || !_.size(rows.filter((row) => row.isChecked && !row.alreadySelected));
  });

  constructor() {
    this.searchTermChange$
      .pipe(
        tap((term) => {
          this.localRows.set(null);
          this.searchTerm.set(term);
        }),
        // Debounce typing by 1s, but flush the latest term immediately on Enter.
        debounce(() => race(timer(1000), this.enterPressed$)),
        filter((term: string) => term?.length > this.SEARCH_TERM_MIN_LENGTH),
        untilDestroyed(this),
      )
      .subscribe((term) => this.fetchData.emit(term));
  }

  cellValue(row: T, column: AutocompleteColumn<T>) {
    if (column.value) {
      return column.value(row);
    }
    // @ts-expect-error -- Column keys may be dynamic strings supplied by consumers.
    return row[column.key];
  }

  getTemplate(key: string | number | symbol) {
    return this.columnTemplates.find((t) => t.columnKey === key);
  }

  onFocus() {
    const term = this.searchTerm();
    if (term && term.length > this.SEARCH_TERM_MIN_LENGTH) {
      this.fetchData.emit(term);
    } else {
      this.localRows.set(null);
    }
  }

  protected onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.localRows.set(null);
      this.focusSearchInput();
    }
  }

  protected onOverlayOutsideClick(event: MouseEvent, origin: HTMLElement): void {
    const target = event.target;
    if (!(target instanceof Node) || !origin.contains(target)) {
      this.localRows.set(null);
    }
  }

  onInputChange(value: string) {
    if (!value) {
      this.reset();
      return;
    }
    this.searchTermChange$.next(value);
  }

  onSelectSingleRowDirectly(row: AutocompleteRow<T>): void {
    if (!row.alreadySelected) {
      this.reset();
      this.selectRow.emit([row.data]);
      this.focusSearchInput();
    }
  }

  onCheck(index: number): void {
    this.localRows.update((rows) => {
      if (!rows) {
        return rows;
      }

      return rows.map((row, i) => {
        if (i === index) {
          return { ...row, isChecked: !row.isChecked };
        }
        if (this.checkboxSelectionType === 'single' && !row.alreadySelected) {
          return { ...row, isChecked: false };
        }
        return row;
      });
    });
  }

  reset(): void {
    this.searchTermChange$.next('');
    this.localRows.set(null);
    this.searchTerm.set(null);
    this.fetchData.emit(null);
  }

  applySelection(): void {
    const rows = this.localRows();
    if (!rows) {
      return;
    }

    const data = rows
      .filter((value: AutocompleteRow<T>) => value.isChecked && !value.alreadySelected)
      .map((guest: AutocompleteRow<T>) => guest.data);

    this.reset();
    this.selectRow.emit(data);
    this.focusSearchInput();
  }

  enterPressed(): void {
    this.enterPressed$.next();
  }

  private focusSearchInput(): void {
    this.findInput(this.searchInput().nativeElement)?.focus();
  }

  private findInput(root: ParentNode): HTMLInputElement | null {
    const input = root.querySelector<HTMLInputElement>('input');
    if (input) {
      return input;
    }

    for (const element of root.querySelectorAll<HTMLElement>('*')) {
      if (element.shadowRoot) {
        const shadowInput = this.findInput(element.shadowRoot);
        if (shadowInput) {
          return shadowInput;
        }
      }
    }

    return null;
  }
}

export interface AutocompleteRow<T> {
  isChecked: boolean;
  alreadySelected: boolean;
  data: T;
}

export interface AutocompleteColumn<T> {
  key: string | keyof T;
  value?: (row: T) => unknown;
  widthInPercent?: string;
  template?: TemplateRef<unknown>;
}
